/**
 * Right-side contextual billing panel (PRD.163 Section 7).
 * Opened from VisitBillingChip. Keeps Invoices and Payments distinct.
 * Uses the shared RightDrawer so the user can keep working on the
 * consultation while the panel is open (same pattern as the Vitals drawer).
 */

import { NavIcon, RightDrawer } from '../../../shell/shared';
import { GuidedInvoiceSummary } from './GuidedInvoiceSummary';
import { GuidedPaymentSummary } from './GuidedPaymentSummary';

// ─── Props ───

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: string;
  encounterId: string;
  visitId: string;
}

// ─── Component ───

export function VisitBillingPanel({ open, onClose, patientId, encounterId, visitId }: Props) {
  return (
    <RightDrawer open={open} onClose={onClose} title="Billing" icon="credit-card" expandable>
      {/* ── Invoices section ── */}
      <SectionLabel icon="file-text" title="Invoices" subtitle="What is being charged" />
      <GuidedInvoiceSummary
        patientId={patientId}
        encounterId={encounterId}
        visitId={visitId}
        singlePane
      />

      {/* ── Payments section ── */}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 'var(--sp-4)' }}>
        <SectionLabel icon="credit-card" title="Payments" subtitle="What has been paid" />
        <GuidedPaymentSummary patientId={patientId} encounterId={encounterId} />
      </div>
    </RightDrawer>
  );
}

// ─── Section label ───

function SectionLabel({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 'var(--sp-3) 0 var(--sp-1)',
      }}
    >
      <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
        <NavIcon name={icon} />
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--text)',
        }}
      >
        {title}
      </span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
        · {subtitle}
      </span>
    </div>
  );
}
